// =============================================================================
// Odczyty organu druku — WYŁĄCZNIE przez RLS `tenant_read` klientem Supabase
// powłoki (jak SitesPage/DevicesPage): zero nowych endpointów do czytania.
// Każde zapytanie zawęża `.eq('organization_id', …)` — RLS też by to zrobił,
// ale jawny filtr to żelazna reguła multitenancy tego repo, nie optymalizacja.
//
// Kolejka (D10): Supabase Realtime na `print_jobs` Z OBOWIĄZKOWYM fallbackiem
// poll 15 s — dopóki `print_jobs` nie wejdzie do publikacji `supabase_realtime`
// (migracja M2), kanał nigdy nie osiągnie SUBSCRIBED i bez fallbacku zakładka
// byłaby martwa. Nadzorca kanału (`uruchomNasluchZadan`) jest wydzielony
// i eksportowany, żeby dało się go przetestować bez Reacta i bez Supabase.
// =============================================================================

import type { PrintJobStatus, SladDruku } from '@insolitum/types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../providers/AuthProvider';

import { LIMIT_SUBSKRYPCJI_MS, POLL_FALLBACK_MS } from './status';

// ── Kształty wierszy (kolumny z migracji 186/265/550 + addytywne 623/626) ────

export interface LokalizacjaWiersz {
  id: string;
  name: string;
  is_active: boolean;
}

export interface DrukarkaWiersz {
  id: string;
  organization_id: string;
  agent_id: string | null;
  name: string;
  type: string; // 'network' | 'system' | 'virtual'
  ip: string | null;
  port: number | null;
  protocol: string | null;
  format: string | null;
  driver: string | null;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  /** Migracja 623 (M1) — nazwa kolejki spoolera / celu CUPS. */
  system_name?: string | null;
  /** Migracja 623 (M1) — 'manual' | 'agent' (wpis z discovery czeka na aktywację). */
  source?: string | null;
}

/**
 * Kolumny agenta wybierane JAWNIE — bez `select('*')`, żeby uchwyty odwołania
 * (`token_jti`, `pending_token_jti`) nigdy nie dotarły do przeglądarki. To samo
 * robi `publicAgent()` po stronie print-core; panel nie może być słabszy.
 */
const KOLUMNY_AGENTA =
  'id,organization_id,name,status,device_id,app_version,platform,hostname,' +
  'last_seen_at,registered_at,created_at,revoked_at';

export interface AgentWiersz {
  id: string;
  organization_id: string;
  name: string;
  status: string; // 'pending' | 'active' | 'revoked'
  device_id: string | null;
  app_version: string | null;
  platform: string | null;
  hostname: string | null;
  last_seen_at: string | null;
  registered_at: string | null;
  created_at: string;
  revoked_at: string | null;
}

export interface StatusDrukarkiWiersz {
  printer_id: string;
  agent_id: string | null;
  status: string; // 'ready' | 'offline' | 'error'
  detail: string | null;
  queue_len: number | null;
  reported_at: string;
}

/**
 * Wiersz zadania BEZ `payload`/`payload_url` — bajty wydruku nie są potrzebne
 * monitorowi, a potrafią być duże; drawer pokazuje tylko format i metadane.
 */
const KOLUMNY_ZADANIA =
  'id,document_type,format,status,attempts,error,printer_id,target_agent_id,' +
  'template_id,copies,created_by,claimed_by,created_at,picked_at,printed_at,done_at,slad';

export interface ZadanieWiersz {
  id: string;
  document_type: string;
  format: string;
  status: PrintJobStatus;
  attempts: number;
  error: string | null;
  printer_id: string | null;
  target_agent_id: string | null;
  template_id: string | null;
  copies: number;
  created_by: string | null;
  claimed_by: string | null;
  created_at: string;
  picked_at: string | null;
  printed_at: string | null;
  done_at: string | null;
  slad: SladDruku | null;
}

export interface PrzypisanieWiersz {
  id: string;
  printer_id: string;
  site_id: string | null;
  module: string | null;
  purpose: string | null;
}

// ── Lokalizacje (filtr ?site= nagłówka strony) ───────────────────────────────

export function useSites() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.organization_id;
  const [lokalizacje, setLokalizacje] = useState<LokalizacjaWiersz[]>([]);
  const [loading, setLoading] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) return;
    let aktywny = true;
    setLoading(true);
    const wczytaj = async () => {
      const { data, error } = await supabase
        .from('organization_sites')
        .select('id,name,is_active')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('name');
      if (!aktywny) return;
      if (error) setBlad('Nie udało się wczytać lokalizacji');
      else setLokalizacje((data as LokalizacjaWiersz[]) ?? []);
      setLoading(false);
    };
    void wczytaj();
    return () => {
      aktywny = false;
    };
  }, [orgId]);

  return { lokalizacje, loading, blad };
}

// ── Kartoteka drukarek + raporty statusu ─────────────────────────────────────

export function usePrinters() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.organization_id;
  const [drukarki, setDrukarki] = useState<DrukarkaWiersz[]>([]);
  const [raporty, setRaporty] = useState<Record<string, StatusDrukarkiWiersz>>({});
  const [loading, setLoading] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);

  const odswiez = useCallback(async () => {
    if (!orgId) return;
    setBlad(null);
    const { data, error } = await supabase
      .from('print_printers')
      .select('*')
      .eq('organization_id', orgId)
      .order('name');
    if (error) {
      setBlad('Nie udało się wczytać kartoteki drukarek');
      setLoading(false);
      return;
    }
    setDrukarki((data as DrukarkaWiersz[]) ?? []);

    // Raporty statusu (migracja 626/M4). Defensywnie: dopóki migracja nie
    // wyląduje, zapytanie zwróci błąd — wtedy po prostu nie mamy raportów
    // („nieraportowany"), a kartoteka działa dalej. Panel niczego nie udaje.
    const { data: statusy, error: bladStatusow } = await supabase
      .from('print_printer_status')
      .select('printer_id,agent_id,status,detail,queue_len,reported_at')
      .eq('organization_id', orgId);
    if (!bladStatusow && statusy) {
      const mapa: Record<string, StatusDrukarkiWiersz> = {};
      for (const wiersz of statusy as StatusDrukarkiWiersz[]) {
        mapa[wiersz.printer_id] = wiersz;
      }
      setRaporty(mapa);
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    setLoading(true);
    void odswiez();
  }, [odswiez]);

  return { drukarki, raporty, loading, blad, odswiez };
}

// ── Serwery wydruku (print_agents) ───────────────────────────────────────────

export function useAgents() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.organization_id;
  const [agenci, setAgenci] = useState<AgentWiersz[]>([]);
  const [loading, setLoading] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);

  const odswiez = useCallback(async () => {
    if (!orgId) return;
    setBlad(null);
    const { data, error } = await supabase
      .from('print_agents')
      .select(KOLUMNY_AGENTA)
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false });
    if (error) {
      setBlad('Nie udało się wczytać serwerów wydruku');
    } else {
      setAgenci((data as unknown as AgentWiersz[]) ?? []);
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    setLoading(true);
    void odswiez();
  }, [odswiez]);

  return { agenci, loading, blad, odswiez };
}

/**
 * Liczniki zadań 24 h per serwer (`claimed_by` przechowuje id agenta jako TEXT).
 * Agregacja po stronie panelu — wolumeny dobowe jednej organizacji są małe,
 * a osobny endpoint agregujący byłby przedwczesną infrastrukturą.
 */
export function useLicznikiZadan24h() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.organization_id;
  const [liczniki, setLiczniki] = useState<Record<string, { ok: number; bledy: number }>>({});

  useEffect(() => {
    if (!orgId) return;
    let aktywny = true;
    const od = new Date(Date.now() - 24 * 3600_000).toISOString();
    const policz = async () => {
      const { data, error } = await supabase
        .from('print_jobs')
        .select('claimed_by,status')
        .eq('organization_id', orgId)
        .gte('created_at', od)
        .not('claimed_by', 'is', null)
        .limit(5000);
      if (!aktywny || error || !data) return;
      const mapa: Record<string, { ok: number; bledy: number }> = {};
      for (const wiersz of data as Array<{ claimed_by: string; status: string }>) {
        const wpis = (mapa[wiersz.claimed_by] ??= { ok: 0, bledy: 0 });
        if (wiersz.status === 'done') wpis.ok += 1;
        else if (wiersz.status === 'error') wpis.bledy += 1;
      }
      setLiczniki(mapa);
    };
    void policz();
    return () => {
      aktywny = false;
    };
  }, [orgId]);

  return liczniki;
}

// ── Przypisania (print_printer_assignments) ──────────────────────────────────

export function useAssignments() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.organization_id;
  const [przypisania, setPrzypisania] = useState<PrzypisanieWiersz[]>([]);
  const [loading, setLoading] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);

  const odswiez = useCallback(async () => {
    if (!orgId) return;
    setBlad(null);
    const { data, error } = await supabase
      .from('print_printer_assignments')
      .select('id,printer_id,site_id,module,purpose')
      .eq('organization_id', orgId)
      .order('purpose');
    if (error) setBlad('Nie udało się wczytać przypisań');
    else setPrzypisania((data as PrzypisanieWiersz[]) ?? []);
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    setLoading(true);
    void odswiez();
  }, [odswiez]);

  return { przypisania, loading, blad, odswiez };
}

// ── Nadzorca kanału Realtime z obowiązkowym fallbackiem poll ─────────────────

/** Minimalny przekrój kanału Supabase, jakiego używamy (testowalny fałszywką). */
export interface KanalNasluchu {
  on(
    typ: 'postgres_changes',
    filtr: { event: string; schema: string; table: string; filter: string },
    handler: () => void
  ): KanalNasluchu;
  subscribe(cb: (status: string) => void): unknown;
}

export interface OpcjeNasluchu {
  utworzKanal: () => KanalNasluchu;
  usunKanal: (kanal: KanalNasluchu) => void;
  /** Filtr postgres_changes — zawsze z `organization_id=eq.{org}`. */
  filtr: { schema: string; table: string; filter: string };
  /** Ponowne pobranie danych (wołane na każdą zmianę i na tick polla). */
  odswiez: () => void;
  /** `true` = Realtime dostarcza, `false` = jedziemy na pollu. */
  naZmianeTrybu?: (naZywo: boolean) => void;
  limitSubskrypcjiMs?: number;
  pollMs?: number;
}

/**
 * Uruchamia kanał postgres_changes i pilnuje jedynego niezmiennika, jaki
 * obiecujemy użytkownikowi: DANE ZAWSZE PŁYNĄ. Kanał, który nie osiągnie
 * SUBSCRIBED w `limitSubskrypcjiMs` (3 s), albo później padnie
 * (CHANNEL_ERROR/TIMED_OUT/CLOSED), zostaje przykryty pollem co `pollMs`
 * (15 s). Późne SUBSCRIBED wyłącza poll z powrotem. Zwraca sprzątacza.
 */
export function uruchomNasluchZadan(opcje: OpcjeNasluchu): () => void {
  const limitMs = opcje.limitSubskrypcjiMs ?? LIMIT_SUBSKRYPCJI_MS;
  const pollMs = opcje.pollMs ?? POLL_FALLBACK_MS;

  let zamkniete = false;
  let subskrybowany = false;
  let poll: ReturnType<typeof setInterval> | null = null;

  const startPoll = () => {
    if (zamkniete || poll) return;
    opcje.naZmianeTrybu?.(false);
    poll = setInterval(() => opcje.odswiez(), pollMs);
  };
  const stopPoll = () => {
    if (poll) {
      clearInterval(poll);
      poll = null;
    }
  };

  // Strażnik: brak SUBSCRIBED w limicie = fallback. Nie czekamy na błąd,
  // bo kanał do tabeli spoza publikacji nie zgłasza błędu — po prostu milczy.
  const straznik = setTimeout(() => {
    if (!subskrybowany) startPoll();
  }, limitMs);

  let kanal: KanalNasluchu | null = null;
  try {
    kanal = opcje.utworzKanal();
    kanal
      .on('postgres_changes', { event: '*', ...opcje.filtr }, () => opcje.odswiez())
      .subscribe((status) => {
        if (zamkniete) return;
        if (status === 'SUBSCRIBED') {
          subskrybowany = true;
          clearTimeout(straznik);
          stopPoll();
          opcje.naZmianeTrybu?.(true);
          // Zmiany sprzed chwili subskrypcji przepadły — jedno odświeżenie domyka lukę.
          opcje.odswiez();
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          subskrybowany = false;
          startPoll();
        }
      });
  } catch {
    startPoll();
  }

  return () => {
    zamkniete = true;
    clearTimeout(straznik);
    stopPoll();
    if (kanal) opcje.usunKanal(kanal);
  };
}

// ── Monitor zadań (Kolejka) ──────────────────────────────────────────────────

export interface FiltryZadan {
  status: PrintJobStatus | '';
  dokument: string;
  drukarka: string;
  /** ISO początku okna czasowego albo '' = bez ograniczenia. */
  odIso: string;
}

export interface StatystykiZadan {
  wKolejce: number;
  drukuje: number;
  dzisiaj: number;
  bledy24h: number;
}

export function usePrintJobs(filtry: FiltryZadan) {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.organization_id;
  const [zadania, setZadania] = useState<ZadanieWiersz[]>([]);
  const [statystyki, setStatystyki] = useState<StatystykiZadan>({
    wKolejce: 0,
    drukuje: 0,
    dzisiaj: 0,
    bledy24h: 0,
  });
  const [naZywo, setNaZywo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);

  const odswiez = useCallback(async () => {
    if (!orgId) return;
    setBlad(null);

    let zapytanie = supabase
      .from('print_jobs')
      .select(KOLUMNY_ZADANIA)
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (filtry.status) zapytanie = zapytanie.eq('status', filtry.status);
    if (filtry.dokument) zapytanie = zapytanie.eq('document_type', filtry.dokument);
    if (filtry.drukarka) zapytanie = zapytanie.eq('printer_id', filtry.drukarka);
    if (filtry.odIso) zapytanie = zapytanie.gte('created_at', filtry.odIso);

    const { data, error } = await zapytanie;
    if (error) {
      setBlad('Nie udało się wczytać zadań druku');
      setLoading(false);
      return;
    }
    setZadania((data as unknown as ZadanieWiersz[]) ?? []);

    // Statystyki liczone OSOBNO (count po indeksowanych kolumnach), bo lista
    // jest przycięta do 50 wierszy i licznik z niej byłby kłamstwem.
    const poczatekDnia = new Date();
    poczatekDnia.setHours(0, 0, 0, 0);
    const dobaTemu = new Date(Date.now() - 24 * 3600_000).toISOString();
    const zbuduj = () =>
      supabase
        .from('print_jobs')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId);
    const licz = async (
      buduj: (q: ReturnType<typeof zbuduj>) => ReturnType<typeof zbuduj>
    ): Promise<number> => {
      const { count } = await buduj(zbuduj());
      return count ?? 0;
    };

    const [wKolejce, drukuje, dzisiaj, bledy24h] = await Promise.all([
      licz((q) => q.eq('status', 'queued')),
      licz((q) => q.in('status', ['picked', 'printing'])),
      licz((q) => q.eq('status', 'done').gte('done_at', poczatekDnia.toISOString())),
      licz((q) => q.eq('status', 'error').gte('created_at', dobaTemu)),
    ]);
    setStatystyki({ wKolejce, drukuje, dzisiaj, bledy24h });
    setLoading(false);
  }, [orgId, filtry.status, filtry.dokument, filtry.drukarka, filtry.odIso]);

  // Ref na odświeżanie: subskrypcja Realtime żyje per organizacja, nie per
  // zestaw filtrów — zmiana filtra nie może zrywać i wznawiać kanału.
  const odswiezRef = useRef(odswiez);
  odswiezRef.current = odswiez;

  useEffect(() => {
    setLoading(true);
    void odswiez();
  }, [odswiez]);

  useEffect(() => {
    if (!orgId) return;
    return uruchomNasluchZadan({
      utworzKanal: () => supabase.channel(`print-jobs:${orgId}`) as unknown as KanalNasluchu,
      usunKanal: (kanal) =>
        void supabase.removeChannel(
          kanal as unknown as Parameters<typeof supabase.removeChannel>[0]
        ),
      filtr: { schema: 'public', table: 'print_jobs', filter: `organization_id=eq.${orgId}` },
      odswiez: () => void odswiezRef.current(),
      naZmianeTrybu: setNaZywo,
    });
  }, [orgId]);

  return { zadania, statystyki, naZywo, loading, blad, odswiez };
}
